import { ProductTypeName } from '@electricjs/core_entity-client';
import { CatalogCartEntry } from '@/types/storefront';
import { Product } from '@/types/products';

/**
 * Returns the most expensive extended warranty, or the default warranty if no extended warranties exist.
 *
 * @param warranties - List of available warranties
 * @param defaultWarrantyId - The ID of the default warranty
 * @returns The warranty that auto-upgrade would select
 */
export const getAutoUpgradedWarranty = (
  warranties: Product[],
  defaultWarrantyId?: string
): Product | undefined => {
  if (warranties.length === 0) return undefined;

  const extendedWarranties = warranties.filter(
    w => w.id !== defaultWarrantyId && w.price !== undefined
  );

  if (extendedWarranties.length === 0) {
    return warranties.find(w => w.id === defaultWarrantyId);
  }

  return extendedWarranties.reduce((max, current) => {
    const [maxPrice, currentPrice] = [max.price ?? 0, current.price ?? 0];
    return currentPrice > maxPrice ? current : max;
  });
};

/**
 * Creates a catalog cart entry with warranty selection logic.
 * This utility is shared between manual cart additions (Storefront UI) and
 * AI-driven recommendations (Gigawatt chat).
 *
 * @param product - The product to add to cart
 * @param options - Optional configuration
 * @param options.explicitWarrantyId - Specific warranty to select (overrides default)
 * @param options.quantity - Quantity to add (defaults to 1)
 * @param options.shouldUpgradeWarranty - Whether to automatically select the most expensive warranty
 * @returns A CatalogCartEntry ready to be added to cart
 *
 * @example
 * // With default warranty
 * const entry = createCatalogCartEntry(product);
 *
 * @example
 * // With explicit warranty (e.g., from AI recommendation)
 * const entry = createCatalogCartEntry(product, {
 *   explicitWarrantyId: 'warranty-premium-123'
 * });
 *
 * @see src/components/Storefront/components/Catalog/CatalogStore.tsx - Manual UI additions
 */
export const createCatalogCartEntry = (
  product: Product,
  options?: {
    explicitWarrantyId?: string;
    quantity?: number;
    shouldUpgradeWarranty?: boolean;
  }
): CatalogCartEntry => {
  const {
    explicitWarrantyId,
    quantity = 1,
    shouldUpgradeWarranty = false,
  } = options ?? {};

  let selectedWarranty: Product | undefined;

  // First, try to select explicit warranty if provided (e.g., from AI recommendation)
  // Skip if the warranty has no price and is not the default (priceless non-default warranties are not orderable)
  if (explicitWarrantyId && product.availableWarranties?.list) {
    const candidate = product.availableWarranties.list.find(
      warranty => warranty.id === explicitWarrantyId
    );
    const isDefault =
      candidate?.id === product.availableWarranties.defaultWarrantyId;
    if (candidate && (isDefault || candidate.price !== undefined)) {
      selectedWarranty = candidate;
    }
  }

  // If no explicit warranty and upgrade preference is true, select the most expensive extended warranty
  if (
    !selectedWarranty &&
    shouldUpgradeWarranty &&
    product.availableWarranties?.list
  ) {
    selectedWarranty = getAutoUpgradedWarranty(
      product.availableWarranties.list,
      product.availableWarranties.defaultWarrantyId
    );
  }

  // Fall back to default warranty if no explicit warranty or upgrade preference is false
  if (!selectedWarranty && product.availableWarranties?.defaultWarrantyId) {
    selectedWarranty = product.availableWarranties.list?.find(
      warranty => warranty.id === product.availableWarranties?.defaultWarrantyId
    );
  }

  return {
    item: { ...product },
    quantity,
    selectedWarranty,
  };
};

/**
 * Filters out warranty products from a product array.
 * Warranty products should not be added as standalone cart items - they should
 * only be attached to their parent products via the selectedWarranty field.
 *
 * @param products - Array of products to filter
 * @returns Products excluding warranties
 *
 * @example
 * const productsOnly = filterOutWarranties(allProducts);
 * // Returns products without productType === 'warranty'
 */
export const filterOutWarranties = (products: Product[]): Product[] => {
  return products.filter(p => p.productType !== ProductTypeName.Warranty);
};

/**
 * Extracts warranty IDs from an array of product IDs and creates a mapping
 * to their parent products. This is useful when AI provides a mix of product
 * and warranty IDs, where specific warranties should be selected instead of defaults.
 *
 * @param products - All fetched products (including warranties in their availableWarranties)
 * @param productIds - Array of IDs that may include warranty IDs
 * @returns Map of productId -> warrantyId for products with explicit warranties
 *
 * @example
 * const warrantyMap = extractWarrantyMappings(products, ['prod-1', 'warranty-premium']);
 * // Returns: Map { 'prod-1' => 'warranty-premium' }
 * // Use with createCatalogCartEntry(product, { explicitWarrantyId: warrantyMap.get(product.id) })
 */
export const extractWarrantyMappings = (
  products: Product[],
  productIds: string[]
): Map<string, string> => {
  const warrantyMap = new Map<string, string>();

  products.forEach(product => {
    const explicitWarranty = product.availableWarranties?.list?.find(warranty =>
      productIds.includes(warranty.id)
    );

    if (explicitWarranty) {
      warrantyMap.set(product.id, explicitWarranty.id);
    }
  });

  return warrantyMap;
};
